import { useState } from "react";
import { Control, FieldArrayWithId, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, ChevronRight, Pencil, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  EditableImage,
  EditableProduct,
  SmartUploadFormValues,
} from "@/schema/smartupload.schema";
import { useSmartUploadStore } from "@/store/smartupload.store";
import { useBrandStore } from "@/store/brand.store";
import { uploadFileAndReturnUrl } from "@/services/api/gcs.service";
import { toast } from "sonner";
import { VariantDropZone } from "./VariantDropZone";

// ─── ProductCard ──────────────────────────────────────────────────────────────

interface ProductCardProps {
  control: Control<SmartUploadFormValues>;
  index: number;
  field: FieldArrayWithId<SmartUploadFormValues, "products">;
  onUpdate: (index: number, value: EditableProduct) => void;
}

type EditingField = "name" | "sku_code";

export function ProductCard({
  control,
  index,
  field,
  onUpdate,
}: ProductCardProps) {
  const { setValue, watch, getValues } = useFormContext<SmartUploadFormValues>();
  const currentName = watch(`products.${index}.name`);
  const currentSku = watch(`products.${index}.sku_code`);
  const watchedVariants = watch(`products.${index}.variants`);
  const [editing, setEditing] = useState<{
    field: EditingField;
    originalValue: string;
  } | null>(null);

  const [editingVariant, setEditingVariant] = useState<{
    vIdx: number;
    originalValue: string;
  } | null>(null);

  const startEditing = (f: EditingField, currentValue: string) =>
    setEditing({ field: f, originalValue: currentValue });

  const confirmEdit = () => setEditing(null);

  const cancelEdit = () => {
    if (!editing) return;
    setValue(`products.${index}.${editing.field}`, editing.originalValue);
    setEditing(null);
  };

  const removeImage = useSmartUploadStore((s) => s.removeImage);
  const { selectedBrandId } = useBrandStore();
  const [isUploading, setIsUploading] = useState(false);

  const handleAddFile = async (vIdx: number, file: File) => {
    setIsUploading(true);
    try {
      const url = await uploadFileAndReturnUrl(file.name, file.type, "product-sku", file, selectedBrandId);
      const current = getValues(`products.${index}`);
      const updatedVariants = current.variants.map((v, i) =>
        i !== vIdx ? v : { ...v, images: [...v.images, { id: crypto.randomUUID(), product_url: url, angle: "front" }] }
      );
      onUpdate(index, { ...current, variants: updatedVariants });
      toast.success("Image added");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (vIdx: number, imgIndex: number, img: EditableImage) => {
    const variant = field.variants[vIdx];
    const current = getValues(`products.${index}`);
    const updatedVariants = current.variants.map((v, i) =>
      i === vIdx ? { ...v, images: v.images.filter((im) => im.id !== img.id) } : v
    );
    onUpdate(index, { ...current, variants: updatedVariants });
    removeImage({
      image: img,
      productId: current.id,
      productName: current.name,
      variantId: variant.id,
      variantName: variant.name,
      originalIndex: imgIndex,
    });
  };

  const thumbnail = field.variants[0]?.images[0]?.product_url ?? "";
  const totalImages = field.variants.reduce((sum, v) => sum + v.images.length, 0);

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      {/* Card header */}
      <div className="flex items-start gap-3 p-4">
        {/* Collapse toggle */}
        <button
          type="button"
          className="mt-1 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          onClick={() =>
            onUpdate(index, { ...getValues(`products.${index}`), collapsed: !field.collapsed })
          }
        >
          {field.collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {/* Thumbnail */}
        <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 shrink-0">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={field.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200" />
          )}
        </div>

        {/* Name + SKU + badges */}
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Name row */}
          <div className="flex items-center gap-1.5">
            {editing?.field === "name" ? (
              <>
                <FormField
                  control={control}
                  name={`products.${index}.name`}
                  render={({ field: f }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...f}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          style={{ width: `max(${(f.value?.length ?? 0) + 3}ch, 80px)` }}
                          className="h-7 text-sm font-semibold px-2 py-0 rounded-lg border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={confirmEdit}
                  className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelEdit}
                  className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <span className="font-semibold text-sm truncate">
                  {currentName}
                </span>
                <button
                  type="button"
                  onClick={() => startEditing("name", currentName)}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>

          {/* SKU row */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground shrink-0">SKU:</span>
            {editing?.field === "sku_code" ? (
              <>
                <FormField
                  control={control}
                  name={`products.${index}.sku_code`}
                  render={({ field: f }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...f}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          style={{ width: `max(${(f.value?.length ?? 0) + 3}ch, 60px)` }}
                          className="h-6 text-xs px-1.5 py-0 rounded-md border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={confirmEdit}
                  className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelEdit}
                  className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : (
              <>
                <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">
                  {currentSku || "—"}
                </span>
                <button
                  type="button"
                  onClick={() => startEditing("sku_code", currentSku)}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 pt-0.5">
            <Badge variant="outline" className="rounded-full text-black-600">
              <strong>{field.variants.length}</strong>&nbsp;variant{field.variants.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        </div>
      </div>

      {/* Expanded body — variants */}
      {!field.collapsed && (
        <div className="px-4 pb-4 space-y-4">
          {field.variants.map((variant, vIdx) => (
            <div key={variant.id} className="space-y-2">
              {/* Variant header */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground shrink-0">Variant:</span>
                {editingVariant?.vIdx === vIdx ? (
                  <>
                    <FormField
                      control={control}
                      name={`products.${index}.variants.${vIdx}.name`}
                      render={({ field: f }) => (
                        <FormItem className="space-y-0">
                          <FormControl>
                            <Input
                              {...f}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingVariant(null);
                                if (e.key === "Escape") {
                                  setValue(`products.${index}.variants.${vIdx}.name`, editingVariant.originalValue);
                                  setEditingVariant(null);
                                }
                              }}
                              style={{ width: `max(${(f.value?.length ?? 0) + 3}ch, 80px)` }}
                              className="h-6 text-xs px-1.5 py-0 rounded-md border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setEditingVariant(null)}
                      className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                    >
                      <Check className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setValue(`products.${index}.variants.${vIdx}.name`, editingVariant.originalValue);
                        setEditingVariant(null);
                      }}
                      className="shrink-0 text-gray-400 hover:text-white hover:bg-blue-500 p-0.5 rounded-full transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-semibold truncate">{watchedVariants?.[vIdx]?.name ?? variant.name}</span>
                    <button
                      type="button"
                      onClick={() => setEditingVariant({ vIdx, originalValue: variant.name })}
                      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </>
                )}
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Image grid — droppable + sortable */}
              <VariantDropZone
                variant={variant}
                vIdx={vIdx}
                productIndex={index}
                onRemove={handleRemoveImage}
                onAddFile={handleAddFile}
                isAdding={isUploading}
                control={control}
                formIndex={index}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
